import { API_CALL } from '../middleware/api';

export const authActions = {
  login: 'auth/login',
  sessionLogout: 'auth/session-logout',
  profile: 'auth/profile',
  extendSession: 'auth/extend-session',
  sessionStatus: 'auth/session-status',
  clearLoginForm: 'auth/clear-login-form',
  oauth2Login: 'auth/fhir-auth',
  ssoLogin: 'auth/sso',
  sendResetPasswordValidationMessage: 'auth/send-reset-pass-valid-message',
  resetPassword: 'auth/reset-password',
  checkSmsCode: 'auth/check-sms-code',
  two_factor_auth: 'auth/2fa',
  two_factor_auth_resend: 'auth/2fa_resend',
  two_factor_auth_resend_result: 'auth/2fa_resend/result',
  clear_two_factor_key: 'auth/clear-2fa-key',
  clear_force_forgot_password: 'auth/clear-force-forgot-password',
  verify_user: 'auth/verify-user',
  resolveLogin: 'auth/resolve-login',
  // Role switching actions
  roleSwitchingMode: 'auth/role-switching-mode',
  switchableRoles: 'auth/switchable-roles',
  currentRole: 'auth/current-role',
  switchRole: 'auth/switch-role',
  resetRole: 'auth/reset-role',
  add_recently_switched_role: 'auth/add-recently-switched-role',
};

export const clear_two_factor_key = () => ({ type: authActions.clear_two_factor_key });

export const clear_force_forgot_password = () => ({ type: authActions.clear_force_forgot_password });

export const resolveLogin = (identifier) => ({
  type: authActions.resolveLogin,
  [API_CALL]: {
    endpoint: 'portal/v1/saml/resolve-login',
    data: { identifier },
    method: 'post',
  },
});

export const two_factor_auth = ({ code, two_factor_key, is_recovery_code = false }) => ({
  type: authActions.two_factor_auth,
  [API_CALL]: {
    endpoint: 'portal/auth/2fa/verify',
    data: {
      code,
      two_factor_token: two_factor_key,
      is_recovery_code,
      portal_type: 'portal_provider',
    },
    method: 'post',
  },
});

export const two_factor_auth_resend = ({ two_factor_key }) => ({
  type: authActions.two_factor_auth_resend,
  [API_CALL]: {
    endpoint: 'portal/auth/2fa/resend',
    data: {
      two_factor_token: two_factor_key,
    },
    method: 'post',
  },
});

export const login = ({ username, email, mobile, password }) => ({
  type: authActions.login,
  [API_CALL]: {
    endpoint: 'portal/auth/login',
    data: {
      ...(username && { username }),
      ...(email && { email }),
      ...(mobile && { mobile }),
      password,
      portal_type: 'portal_provider',
    },
    method: 'post',
  },
});

export const verifyUser = ({ token, code }) => ({
  type: authActions.verify_user,
  [API_CALL]: {
    endpoint: '/v2/user-verify/',
    data: {
      token,
      code,
    },
    method: 'post',
  },
});

export const oauth2Login = ({ sst }) => ({
  type: authActions.oauth2Login,
  [API_CALL]: {
    endpoint: 'portal/fhir/login',
    data: { sst },
    method: 'post',
  },
});

export const ssoLogin = ({ token, context, user, source = '' }) => ({
  type: authActions.ssoLogin,
  [API_CALL]: {
    endpoint: 'portal/sso/login',
    data: {
      token,
      context,
      user,
    },
    method: 'post',
    source,
  },
});

export const userProfile = () => {
  // Use cached dispatch to avoid duplicate calls to users/me
  // Cache for 30 seconds since profile data changes infrequently
  return dispatch => {
    return import('../utils/apiCache').then(({ cachedDispatch }) => {
      return cachedDispatch(
        'users_me',
        {},
        () =>
          dispatch({
            type: authActions.profile,
            [API_CALL]: {
              endpoint: 'portal/api/accounts/users/me/',
              data: null,
            },
          }),
        30000, // Cache for 30 seconds
      );
    });
  };
};

export const sessionLogout = () => ({
  type: authActions.sessionLogout,
  [API_CALL]: {
    endpoint: 'portal/auth/logout',
    method: 'post',
  },
});

export const extendSession = () => ({
  type: authActions.extendSession,
  [API_CALL]: {
    endpoint: 'fa/sessions/extend',
    method: 'post',
  },
});

export const sessionStatus = () => ({
  type: authActions.sessionStatus,
  [API_CALL]: {
    endpoint: 'fa/sessions/logged_in',
    method: 'get',
  },
});

export const clearLoginForm = { type: authActions.clearLoginForm };

export const unSetError = () => ({ type: 'auth/status/unset' });

export const sendRestPasswordMessage = model => ({
  type: authActions.sendResetPasswordValidationMessage,
  [API_CALL]: {
    endpoint: 'portal/accounts/user/password/change/',
    method: 'post',
    data: model,
  },
});

export const resetPassword = model => ({
  type: authActions.resetPassword,
  [API_CALL]: {
    endpoint: 'portal/accounts/user/password/verify/',
    method: 'post',
    data: model,
  },
});

export const resetPasswordWithLogin = (token, password) => ({
  type: authActions.login,
  [API_CALL]: {
    endpoint: 'portal/auth/reset-password',
    method: 'post',
    data: {
      token,
      password,
      login: true,
      portal_type: 'portal_provider',
    },
  },
});

export const checkSmsCode = model => ({
  type: authActions.checkSmsCode,
  [API_CALL]: {
    endpoint: 'portal/accounts/user/password/verify_sms/',
    method: 'post',
    data: model,
  },
});

// Role switching constants
export const ALL_FACILITIES_UUID = '00000000-0000-0000-0000-000000000000';

// Get role switching mode for the current user
export const getRoleSwitchingMode = () => ({
  type: authActions.roleSwitchingMode,
  [API_CALL]: {
    endpoint: 'portal/auth/role-switching-mode',
    method: 'get',
  },
});

// Get all switchable organizations, roles, and facilities
export const getSwitchableRoles = () => ({
  type: authActions.switchableRoles,
  [API_CALL]: {
    endpoint: 'portal/auth/switchable',
    method: 'get',
  },
});

// Get current effective role and organization
export const getCurrentRole = () => ({
  type: authActions.currentRole,
  [API_CALL]: {
    endpoint: 'portal/auth/role',
    method: 'get',
  },
});

// Switch to a different role/org/facility
export const switchRole = ({ roleUuid, orgUuid, facilityUuid }) => {
  const payload = {
    role: {
      role_uuid: roleUuid,
      org_uuid: orgUuid,
    },
  };

  // Add facility if provided and not "all facilities"
  if (facilityUuid && facilityUuid !== ALL_FACILITIES_UUID) {
    payload.role.facility_uuid = facilityUuid;
  }

  return {
    type: authActions.switchRole,
    [API_CALL]: {
      endpoint: 'portal/auth/role',
      method: 'put',
      data: payload,
    },
  };
};

// Reset to native role
export const resetToNativeRole = () => ({
  type: authActions.resetRole,
  [API_CALL]: {
    endpoint: 'portal/auth/role',
    method: 'delete',
  },
});

export const addRoleToRecentlySwitched = roleInfo => ({
  type: authActions.add_recently_switched_role,
  payload: roleInfo,
});
